import api from '../config/api';

export interface Advertisement {
  id: number;
  title: string;
  advertiser_name: string;
  advertiser_email: string;
  advertiser_phone?: string;
  image: string;
  link_url: string;
  description?: string;
  position: string;
  position_display: string;
  start_date: string;
  end_date?: string;
  status: string;
  status_display: string;
  impressions: number;
  clicks: number;
  click_through_rate: number;
  price: number;
  payment_status: string;
  payment_status_display: string;
  payment_date?: string;
  is_active: boolean;
}

export interface AdvertisementPublic {
  id: number;
  title: string;
  image: string;
  link_url: string;
  position: string;
  description?: string;
}

export interface AdvertisementStats {
  total_ads: number;
  active_ads: number;
  total_impressions: number;
  total_clicks: number;
  total_revenue: number;
  by_position: Array<{ position: string; count: number }>;
  by_status: Array<{ status: string; count: number }>;
}

export interface PaginatedResponse<T> {
  results: T[];
  count: number;
  next: string | null;
  previous: string | null;
}

export const advertisementService = {
  getAll: async (page: number = 1): Promise<Advertisement[] | PaginatedResponse<Advertisement>> => {
    const response = await api.get('/advertisements/advertisements/', { params: { page } });
    // Si pagination activée, retourner les données paginées
    if (response.data.results) {
      return response.data;
    }
    // Sinon, retourner un tableau simple
    return Array.isArray(response.data) ? response.data : [];
  },

  getById: async (id: number): Promise<Advertisement> => {
    const response = await api.get(`/advertisements/advertisements/${id}/`);
    return response.data;
  },

  getActive: async (): Promise<AdvertisementPublic[]> => {
    const response = await api.get('/advertisements/advertisements/list_active/');
    return response.data;
  },

  getByPosition: async (position: string): Promise<AdvertisementPublic[]> => {
    const response = await api.get(`/advertisements/advertisements/by-position/${position}/`);
    return response.data;
  },

  trackImpression: async (id: number): Promise<void> => {
    await api.post(`/advertisements/advertisements/${id}/track_impression/`);
  },

  trackClick: async (id: number): Promise<{ clicks: number; redirect_url: string }> => {
    const response = await api.post(`/advertisements/advertisements/${id}/track_click/`);
    return response.data;
  },

  getStats: async (): Promise<AdvertisementStats> => {
    const response = await api.get('/advertisements/advertisements/stats/');
    return response.data;
  },

  create: async (data: FormData | Partial<Advertisement>): Promise<Advertisement> => {
    // Pour FormData, ne pas spécifier Content-Type, axios le fait automatiquement
    const response = await api.post('/advertisements/advertisements/', data);
    return response.data;
  },

  update: async (id: number, data: FormData | Partial<Advertisement>): Promise<Advertisement> => {
    // Pour FormData, ne pas spécifier Content-Type, axios le fait automatiquement
    const response = await api.patch(`/advertisements/advertisements/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/advertisements/advertisements/${id}/`);
  },
};

